/**
 * STYLING: Plain Tailwind ONLY!
 * Workspace brand picker for text tools — the chosen brand's AI description
 * (Product Brands → "KI-Beschreibung", single source of truth) is injected
 * as the voice context by the backend.
 * Hidden when the workspace has no brands — no UI noise for empty state.
 * Brands without an ai_description are still selectable but have no effect.
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { Select } from '@/components/v2/ui/Select';
import { getBrands } from '@/lib/api/brands';
import type { Brand } from '@/types/brands';

const NONE_VALUE = '__none__';

interface BrandPickerProps {
  id: string;
  value: string | null;
  onChange: (id: string | null) => void;
}

export function BrandPicker({ id, value, onChange }: BrandPickerProps) {
  const { t } = useTranslation();
  const { data: brands, isLoading } = useQuery({
    queryKey: ['brands'],
    queryFn: () => getBrands(),
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading || !brands || brands.length === 0) return null;

  const options = [
    { value: NONE_VALUE, label: t('textTools.brand.none') },
    ...brands.map((b: Brand) => ({ value: b.id, label: b.name })),
  ];

  return (
    <div className="text-tool-brand flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50/40 p-3 dark:border-amber-800/60 dark:bg-amber-950/20">
      <Sparkles className="h-4 w-4 text-amber-700 dark:text-amber-300" aria-hidden="true" />
      <div className="flex-1">
        <Select
          id={id}
          label={t('textTools.brand.label')}
          helperText={t('textTools.brand.helperText')}
          value={value ?? NONE_VALUE}
          onValueChange={(v) => onChange(v === NONE_VALUE ? null : v)}
          options={options}
        />
      </div>
    </div>
  );
}
